<template>
    <div>
        <!-- Active Business Point Events Table -->
        <div v-if="checkPermission('coopbusinesspointevent_activeindex')">
            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
            />
        </div>

        <!-- Quick Apply Modal -->
        <CommonModal ref="quickApplyModal" size="lg" :onHideOnlyX="true">
            <template v-slot:CommonModalTitle>
                {{ selectedEvent ? selectedEvent.speaker_name : '' }} - {{ selectedEvent ? selectedEvent.company_name : '' }}
            </template>
            <template v-slot:CommonModalContent>
                <div class="p-2" v-if="selectedEvent">
                    <!-- Slots Selection Grid Container -->
                    <div class="calendar-container p-4 rounded mb-4" style="background-color: #f7f3ed; border: 1px solid #e6dfd5;">
                        <h6 class="font-weight-bold mb-3 text-dark">{{ $t('coop_appointment_time') }}</h6>
                        <b-row class="text-center bg-white p-3 rounded shadow-sm mx-0" v-if="slots && slots.length > 0">
                            <b-col v-for="slot in slots" :key="slot.start_time || slot.time" cols="3" class="px-2 mb-2">
                                <b-button size="sm"
                                          block
                                          :variant="getSlotVariant(slot)"
                                          :disabled="isSlotDisabled(slot)"
                                          :class="['py-2 font-weight-bold slot-btn', isSlotSelected(slot) ? 'selected-slot' : '']"
                                          @click="selectSlot(slot)">
                                    {{ formatSlotLabel(slot) }}
                                </b-button>
                            </b-col>
                        </b-row>
                        <div v-else class="text-muted text-center py-3">
                            -
                        </div>
                    </div>

                    <!-- CV Selection -->
                    <b-row class="mb-4">
                        <b-col md="12">
                            <b-form-group :label="$t('coop_select_cv')" class="font-weight-bold">
                                <b-form-select v-model="selectedCv" :options="cvOptions">
                                    <template #first>
                                        <b-form-select-option :value="null" disabled>-- {{ $t('coop_select_cv') }} --</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <!-- Save Action -->
                    <div class="text-center">
                        <b-button variant="primary"
                                  class="px-5 font-weight-bold shadow"
                                  :disabled="!selectedSlot || !selectedCv || isSubmitting"
                                  @click="submitBooking">
                            {{ $t('save') }}
                        </b-button>
                    </div>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
    import Datatable from '@/components/datatable/Datatable';
    import CommonModal from '@/components/elements/CommonModal';
    import CoopBusinessPointEventService from '@/services/CoopBusinessPointEventService';
    import CoopBusinessPointApplicationService from '@/services/CoopBusinessPointApplicationService';
    import CoopCvService from '@/services/CoopCvService';
    import qs from 'qs';

    export default {
        name: 'BusinessPointEvents',
        components: {
            Datatable,
            CommonModal
        },
        data() {
            return {
                isSubmitting: false,
                cvList: [],
                slots: [],
                selectedEvent: null,
                selectedSlot: null,
                selectedCv: null,
                datatable: {
                    isLoading: false,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_quick_apply'),
                                    class: 'ri-flashlight-line text-warning',
                                    permission: 'coopbusinesspointapplication_store',
                                    callback: (row) => {
                                        this.openQuickApplyModal(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('company_name'),
                            field: 'company_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_speaker_name'),
                            field: 'speaker_name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_event_date'),
                            field: 'event_date',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_start_time'),
                            field: 'start_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_end_time'),
                            field: 'end_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_slot_duration'),
                            field: 'slot_duration_minutes',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        computed: {
            cvOptions() {
                return this.cvList.map(cv => ({
                    value: cv.id,
                    text: cv.title || cv.name || `CV #${cv.id}`
                }));
            }
        },
        created() {
            this.getRows();
            this.loadCvs();
        },
        methods: {
            loadCvs() {
                CoopCvService.getAll()
                    .then((response) => {
                        this.cvList = response.data.data || [];
                    })
                    .catch(() => {});
            },
            getRows() {
                if (!this.checkPermission('coopbusinesspointevent_activeindex')) {
                    return;
                }
                this.datatable.showTable = true;
                this.datatable.isLoading = true;

                const config = {
                    params: {
                        ...this.datatable.queryParams
                    },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopBusinessPointEventService.getActive(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.getRows();
            },
            onSortChange(params) {
                if (params && params.length > 0) {
                    this.datatable.queryParams.sort = (params[0].type === 'desc' ? '-' : '') + params[0].field;
                }
                this.getRows();
            },
            openQuickApplyModal(row) {
                this.selectedEvent = row;
                this.selectedSlot = null;
                this.selectedCv = null;
                this.slots = [];

                CoopBusinessPointApplicationService.getAvailableSlots(row.id)
                    .then((response) => {
                        this.slots = response.data.data || [];
                        this.$refs.quickApplyModal.$refs.commonModal.show();
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    });
            },
            formatSlotLabel(slot) {
                const startTime = (slot.start_time || slot.time || '').substring(0, 5);
                let endTime = slot.end_time ? slot.end_time.substring(0, 5) : '';
                if (!endTime && startTime) {
                    endTime = moment(startTime, 'HH:mm').add(15, 'minutes').format('HH:mm');
                }
                if (startTime && endTime) {
                    return `${startTime.replace(':', '.')}-${endTime.replace(':', '.')}`;
                }
                return startTime;
            },
            isSlotDisabled(slot) {
                if (slot.status && slot.status !== 'available') return true;
                if (slot.available === false || slot.is_booked || slot.booked) return true;
                return false;
            },
            isSlotSelected(slot) {
                const timeStr = (slot.start_time || slot.time || '').substring(0, 5);
                const selectedStr = (this.selectedSlot || '').substring(0, 5);
                return !!(selectedStr && selectedStr === timeStr);
            },
            getSlotVariant(slot) {
                if (this.isSlotSelected(slot)) return 'warning';
                if (slot.status === 'available' || slot.available === true || slot.available === undefined) return 'outline-success';
                return 'secondary';
            },
            selectSlot(slot) {
                if (this.isSlotDisabled(slot)) return;
                const timeStr = (slot.start_time || slot.time || '').substring(0, 5);
                this.selectedSlot = timeStr;
            },
            submitBooking() {
                if (!this.selectedEvent || !this.selectedSlot || !this.selectedCv) return;

                this.isSubmitting = true;
                const payload = {
                    cv_id: this.selectedCv,
                    slot_start_time: this.selectedSlot
                };

                CoopBusinessPointApplicationService.apply(this.selectedEvent.id, payload)
                    .then((response) => {
                        this.$toast.success(this.$t('api.' + response.data.message));
                        this.$refs.quickApplyModal.$refs.commonModal.hide();
                        this.getRows();
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.isSubmitting = false;
                    });
            }
        }
    };
</script>

<style scoped>
    .selected-slot {
        background-color: #f5a623 !important;
        border-color: #d08717 !important;
        color: #ffffff !important;
        box-shadow: 0 4px 10px rgba(245, 166, 35, 0.4) !important;
        transform: scale(1.03);
        transition: all 0.2s ease-in-out;
    }
</style>
